import {
    DOCUMENT_TABLE_CELL_SPAN_MAXIMUM,
    DOCUMENT_TABLE_CELL_SPAN_MINIMUM,
} from '@gitbook/api-client';

import type {
    BlockTableData,
    NumberColumnDefinition,
    TableCellMerge,
    TableColumnDefinition,
    TextColumnDefinition,
} from '../types';
import { defaultRecordSortedKeys } from './utils';

export interface TableCellPosition {
    recordId: string;
    definitionId: string;
}

export interface ResolvedTableCellMerge {
    merge: TableCellMerge;
    records: string[];
    columns: string[];
}

/** A contiguous range of an axis' order that a single merge occupies. */
export interface CellMergeBand {
    start: number;
    length: number;
}

export type MergeableColumnDefinition = TextColumnDefinition | NumberColumnDefinition;

/** Return a collision-safe identity for a table cell. */
export function tableCellKey(recordId: string, definitionId: string): string {
    return JSON.stringify([recordId, definitionId]);
}

/** Resolve valid, conflict-free merge metadata against the table's current order. */
export function resolveCellMerges(
    data: Pick<BlockTableData, 'cellMerges' | 'definition' | 'records'>,
    recordOrder: readonly string[],
    columnOrder: readonly string[]
): ResolvedTableCellMerge[] {
    if (!Array.isArray(data.cellMerges)) return [];

    const recordIndexById = new Map(recordOrder.map((recordId, index) => [recordId, index]));
    const columnIndexById = new Map(columnOrder.map((columnId, index) => [columnId, index]));
    const candidates: Array<{ resolved: ResolvedTableCellMerge; cellKeys: string[] }> = [];

    for (const candidate of data.cellMerges as unknown[]) {
        const resolved = resolveCellMergeCandidate(
            candidate,
            data,
            recordOrder,
            columnOrder,
            recordIndexById,
            columnIndexById
        );
        if (!resolved) continue;

        candidates.push({
            resolved,
            cellKeys: getCellMergePositions(resolved).map((cell) =>
                tableCellKey(cell.recordId, cell.definitionId)
            ),
        });
    }

    const cellOwnerCounts = new Map<string, number>();
    for (const candidate of candidates) {
        for (const cellKey of candidate.cellKeys) {
            cellOwnerCounts.set(cellKey, (cellOwnerCounts.get(cellKey) ?? 0) + 1);
        }
    }

    return candidates
        .filter((candidate) =>
            candidate.cellKeys.every((cellKey) => cellOwnerCounts.get(cellKey) === 1)
        )
        .map((candidate) => candidate.resolved);
}

/** Return all logical cells occupied by a resolved merge. */
export function getCellMergePositions(merge: ResolvedTableCellMerge): TableCellPosition[] {
    return merge.records.flatMap((recordId) =>
        merge.columns.map((definitionId) => ({ recordId, definitionId }))
    );
}

/** Return whether a column can participate in a merged cell. */
export function isMergeableColumnDefinition(
    definition: TableColumnDefinition
): definition is MergeableColumnDefinition {
    return definition.type === 'text' || definition.type === 'number';
}

/** Return whether a record participates in any valid merged cell. */
export function isRecordPartOfCellMerge(data: BlockTableData, recordId: string): boolean {
    return resolveCurrentCellMerges(data).some((merge) => merge.records.includes(recordId));
}

/** Return whether a column participates in any valid merged cell. */
export function isColumnPartOfCellMerge(data: BlockTableData, columnId: string): boolean {
    return resolveCurrentCellMerges(data).some((merge) => merge.columns.includes(columnId));
}

/** Return whether proposed orders preserve the exact current members of every merge. */
export function preservesCellMergeOrder(
    data: BlockTableData,
    recordOrder: readonly string[],
    columnOrder: readonly string[]
): boolean {
    return resolveCurrentCellMerges(data).every(
        (merge) =>
            isOrderedContiguous(recordOrder, merge.records) &&
            isOrderedContiguous(columnOrder, merge.columns)
    );
}

/** Return whether inserting at an order boundary would avoid splitting a merge. */
export function canInsertAtCellMergeBoundary(
    data: BlockTableData,
    axis: 'records' | 'columns',
    order: readonly string[],
    index: number
): boolean {
    return canInsertAtCellMergeBands(getCellMergeBands(data, axis, order), index);
}

/**
 * Return the index ranges that merges occupy along an axis, so a caller inserting repeatedly
 * can check every boundary without resolving the merges again.
 */
export function getCellMergeBands(
    data: BlockTableData,
    axis: 'records' | 'columns',
    order: readonly string[]
): CellMergeBand[] {
    const bands: CellMergeBand[] = [];

    for (const merge of resolveCurrentCellMerges(data)) {
        const ids = merge[axis];
        if (ids.length < 2) continue;

        const start = order.indexOf(ids[0]);
        if (start < 0) continue;

        bands.push({ start, length: ids.length });
    }

    return bands;
}

/** Return whether inserting at an index would avoid splitting any of the given merge bands. */
export function canInsertAtCellMergeBands(bands: readonly CellMergeBand[], index: number): boolean {
    return bands.every((band) => index <= band.start || index >= band.start + band.length);
}

function resolveCellMergeCandidate(
    candidate: unknown,
    data: Pick<BlockTableData, 'cellMerges' | 'definition' | 'records'>,
    recordOrder: readonly string[],
    columnOrder: readonly string[],
    recordIndexById: ReadonlyMap<string, number>,
    columnIndexById: ReadonlyMap<string, number>
): ResolvedTableCellMerge | null {
    if (!isTableCellMerge(candidate)) return null;

    const isHorizontal = candidate.rowSpan === 1 && candidate.colSpan >= 2;
    const isVertical = candidate.rowSpan >= 2 && candidate.colSpan === 1;
    if (!isHorizontal && !isVertical) return null;

    const recordStart = recordIndexById.get(candidate.anchor.record);
    const columnStart = columnIndexById.get(candidate.anchor.column);
    if (recordStart === undefined || columnStart === undefined) return null;

    const records = recordOrder.slice(recordStart, recordStart + candidate.rowSpan);
    const columns = columnOrder.slice(columnStart, columnStart + candidate.colSpan);
    if (
        records.length !== candidate.rowSpan ||
        columns.length !== candidate.colSpan ||
        records.some((recordId) => !data.records[recordId])
    ) {
        return null;
    }

    const definitions = columns.map((id) => data.definition[id]);
    const columnsCompatible =
        definitions.every(
            (definition): definition is MergeableColumnDefinition =>
                Boolean(definition) && isMergeableColumnDefinition(definition!)
        ) && definitions.every((definition) => definition.type === definitions[0]!.type);
    if (!columnsCompatible) return null;

    return { merge: candidate, records, columns };
}

function resolveCurrentCellMerges(data: BlockTableData): ResolvedTableCellMerge[] {
    // Sorting the records is the expensive part, so skip it for the common merge-free table.
    if (!data.cellMerges?.length) return [];
    return resolveCellMerges(data, defaultRecordSortedKeys(data), data.view.columns);
}

function isTableCellMerge(candidate: unknown): candidate is TableCellMerge {
    if (!candidate || typeof candidate !== 'object') return false;
    const merge = candidate as {
        anchor?: { record?: unknown; column?: unknown };
        rowSpan?: unknown;
        colSpan?: unknown;
    };
    return (
        typeof merge.rowSpan === 'number' &&
        Number.isInteger(merge.rowSpan) &&
        merge.rowSpan >= DOCUMENT_TABLE_CELL_SPAN_MINIMUM &&
        merge.rowSpan <= DOCUMENT_TABLE_CELL_SPAN_MAXIMUM &&
        typeof merge.colSpan === 'number' &&
        Number.isInteger(merge.colSpan) &&
        merge.colSpan >= DOCUMENT_TABLE_CELL_SPAN_MINIMUM &&
        merge.colSpan <= DOCUMENT_TABLE_CELL_SPAN_MAXIMUM &&
        typeof merge.anchor?.record === 'string' &&
        typeof merge.anchor.column === 'string'
    );
}

function isOrderedContiguous(order: readonly string[], ids: readonly string[]): boolean {
    const start = order.indexOf(ids[0]);
    return start >= 0 && ids.every((id, index) => order[start + index] === id);
}
